import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { command } from '@/services/vault/vault.http'
import { isUuid } from '@/services/vault/sourceReviews'
import {
  subjects,
  evidence,
  validDeclaration,
  requestedActions,
  submitVerification,
  VerificationNotRecorded,
  validReceipt,
  ownReceipt,
  commandReceipt,
  requestPath,
  referenceStorageKey,
  type VerificationCommand,
} from '@/services/vault/subjectVerifications'
import { Field, useCommand, CommandStatus, actionLabel } from './VaultForms'

export default function SubjectVerificationPanel() {
  const { user } = useAuth()
  if (
    user?.authScope !== 'user' ||
    !['firm_owner', 'firm_admin', 'ca_partner'].includes(user.role) ||
    !user.permissions?.includes('txh.client.read') ||
    !isUuid(user.id) ||
    !isUuid(user.tenantId) ||
    !isUuid(user.currentSessionId ?? '')
  )
    return null
  // Keep an unknown exact command through permission withdrawal. A different
  // account, firm, session or persona receives no saved private command body.
  return (
    <Panel
      key={JSON.stringify([
        user.id,
        user.tenantId,
        user.authScope,
        user.currentSessionId,
        user.role,
      ])}
    />
  )
}
function Panel() {
  const { user } = useAuth()
  const permissions = user!.permissions ?? []
  const canSource = permissions.includes('txh.assessment.case.read')
  const canEvidence = permissions.includes('txh.document.read')
  const authority = JSON.stringify([
    user!.id,
    user!.tenantId,
    user!.currentSessionId,
    user!.role,
    permissions,
  ])
  const [offset, setOffset] = useState(0),
    [documentPage, setDocumentPage] = useState(1)
  const [selected, setSelected] = useState(''),
    [account, setAccount] = useState(''),
    [capacity, setCapacity] = useState(''),
    [role, setRole] = useState('')
  const [actions, setActions] = useState<string[]>([]),
    [documentIds, setDocumentIds] = useState<string[]>([]),
    [ack, setAck] = useState<string | null>(null)
  const [original, setOriginal] = useState<VerificationCommand | null>(null)
  const storageKey = referenceStorageKey(user!.id, user!.tenantId)
  const [saved, setSaved] = useState(() => {
    try {
      const value = sessionStorage.getItem(storageKey) ?? ''
      return isUuid(value) ? value : ''
    } catch {
      return ''
    }
  })
  const [request, setRequest] = useState(''),
    [requestInput, setRequestInput] = useState('')
  const [statusRead, setStatusRead] = useState(0)
  const [cancelReason, setCancelReason] = useState(''),
    [cancelAck, setCancelAck] = useState<string | null>(null)
  const [originalCancel, setOriginalCancel] = useState<{
    request: string
    body: { expected_ordinal: number; reason: string; idempotency_key: string }
  } | null>(null)
  const feedback = useCommand(),
    inFlight = useRef(false),
    mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const inventory = useQuery({
    queryKey: ['subject-sources', authority, offset],
    queryFn: () => subjects(user!.tenantId, 20, offset),
    enabled: canSource,
    staleTime: 0,
  })
  const documents = useQuery({
    queryKey: ['subject-evidence', authority, documentPage],
    queryFn: () => evidence(user!.tenantId, documentPage, 20),
    enabled: canEvidence && canSource,
    staleTime: 0,
  })
  const current = useQuery({
    queryKey: ['subject-verification-status', authority, request, statusRead],
    queryFn: () => ownReceipt(request),
    enabled: !!request,
    staleTime: 0,
  })
  const sourcePage =
    canSource && !inventory.isFetching && !inventory.isError ? inventory.data : undefined
  const evidencePage =
    canSource && canEvidence && !documents.isFetching && !documents.isError
      ? documents.data
      : undefined
  const source = sourcePage?.items.find(item => item.facts.id === selected)
  const selectedDocuments = evidencePage?.items.filter(item => documentIds.includes(item.id)) ?? []
  const currentReceipt = !current.isFetching && !current.isError ? current.data : undefined
  const ready =
    !!source &&
    !!evidencePage &&
    selectedDocuments.length === documentIds.length &&
    validDeclaration(
      account.trim().toLowerCase(),
      capacity.trim(),
      role,
      actions,
      selectedDocuments
    )
  const binding = ready
    ? JSON.stringify([
        authority,
        source,
        inventory.dataUpdatedAt,
        documents.dataUpdatedAt,
        account,
        capacity,
        role,
        actions,
        selectedDocuments,
      ])
    : null
  const cancellationBinding =
    currentReceipt?.state === 'pending' && cancelReason
      ? JSON.stringify([authority, currentReceipt, current.dataUpdatedAt, cancelReason])
      : null
  const clearDraft = () => {
    setSelected('')
    setAccount('')
    setCapacity('')
    setRole('')
    setActions([])
    setDocumentIds([])
    setAck(null)
  }
  useEffect(() => {
    setSelected('')
    setDocumentIds([])
    setAck(null)
    setCancelAck(null)
  }, [authority, inventory.isFetching, inventory.isError, documents.isFetching, documents.isError])
  useEffect(() => {
    setCancelAck(null)
  }, [current.isFetching, current.isError, current.dataUpdatedAt])
  const remember = (key: string) => {
    setSaved(key)
    try {
      sessionStorage.setItem(storageKey, key)
    } catch {
      /* The displayed opaque reference remains usable. */
    }
  }
  const forget = () => {
    setSaved('')
    try {
      sessionStorage.removeItem(storageKey)
    } catch {
      /* No private body is persisted. */
    }
  }
  const show = (id: string) => {
    setRequest(id)
    setRequestInput(id)
    // Start a new current GET after an acknowledged command. An earlier
    // in-flight inspection may have read the preceding pending state.
    setStatusRead(value => value + 1)
    setCancelReason('')
    setCancelAck(null)
  }
  const send = (payload: VerificationCommand) => {
    if (feedback.busy || inFlight.current) return
    inFlight.current = true
    setOriginal(payload)
    setAck(null)
    remember(payload.idempotency_key)
    void feedback.run(async () => {
      try {
        const receipt = await submitVerification(payload)
        if (!mounted.current) return
        setOriginal(null)
        clearDraft()
        show(receipt.request_id)
      } catch (failure) {
        if (
          mounted.current &&
          failure instanceof VerificationNotRecorded &&
          failure.key === payload.idempotency_key
        ) {
          setOriginal(null)
          forget()
          clearDraft()
          await inventory.refetch()
        }
        throw failure
      } finally {
        inFlight.current = false
      }
    })
  }
  const recover = () => {
    if (!saved || feedback.busy) return
    void feedback.run(async () => {
      const receipt = await commandReceipt(saved)
      if (!mounted.current) return
      setOriginal(null)
      clearDraft()
      show(receipt.request_id)
    })
  }
  const cancel = (payload: NonNullable<typeof originalCancel>) => {
    if (feedback.busy || inFlight.current) return
    inFlight.current = true
    setOriginalCancel(payload)
    setCancelAck(null)
    void feedback.run(async () => {
      try {
        const receipt = await command<unknown>(
          requestPath(payload.request) + '/cancel',
          payload.body
        )
        if (!validReceipt(receipt, payload.request) || receipt.state !== 'cancelled')
          throw new Error(
            'Cancellation is awaiting confirmation. Check status or retry the exact original cancellation.'
          )
        if (!mounted.current) return
        setOriginalCancel(null)
        setCancelReason('')
        show(payload.request)
      } finally {
        inFlight.current = false
      }
    })
  }
  const labels: Record<string, string> = {
    'firm_access.manage': 'Manage firm access',
    'client_switch.manage': 'Manage firm switching',
  }
  return (
    <section
      className="card card-elevated p-5 space-y-4 min-w-0"
      aria-label="Legal subject verification requests"
    >
      <h2 className="text-lg font-semibold">Legal subject verification requests</h2>
      <p className="text-sm text-text-secondary">
        Propose a controller or representative for an original HUF subject. A pending request grants
        no ownership or data access. Legal authority, evidence and the person’s acceptance still
        require verification.
      </p>
      <CommandStatus {...feedback} />
      {saved && (
        <div className="border border-surface-overlay rounded-lg p-4 space-y-3">
          <Field
            label="Original command reference"
            value={saved}
            readOnly
            onFocus={event => event.target.select()}
          />
          <p className="text-sm text-text-secondary">
            Keep this reference to check a lost response. Checking status never sends the private
            form again.
          </p>
          <button className="btn btn-secondary" disabled={feedback.busy} onClick={recover}>
            Check saved command
          </button>
        </div>
      )}
      {original && (
        <div className="space-y-3">
          <p role="status">
            The original request is awaiting confirmation. Retry only that exact request, or check
            its saved command reference.
          </p>
          <button
            className="btn btn-secondary"
            disabled={feedback.busy}
            onClick={() => send(original)}
          >
            Retry original verification request
          </button>
        </div>
      )}
      {!original && (!saved || !!currentReceipt) && (
        <form
          className="space-y-4 min-w-0"
          onSubmit={event => {
            event.preventDefault()
            if (!ready || !source || !binding || ack !== binding || feedback.busy) return
            const body = {
              case_id: source.facts.id,
              expected_source_digest: source.source_digest,
              proposed_account_id: account.trim().toLowerCase(),
              declared_capacity: capacity.trim(),
              requested_role: role as VerificationCommand['requested_role'],
              requested_actions: [...actions].sort(),
              declared_evidence: selectedDocuments
                .map(item => ({ evidence_id: item.id, digest: item.digest! }))
                .sort((a, b) => a.evidence_id.localeCompare(b.evidence_id)),
              acknowledge_unverified_request: true as const,
            }
            send({ ...body, idempotency_key: crypto.randomUUID() })
          }}
        >
          {!canSource && (
            <p role="status">
              Current Case-read permission is required to select a new subject. Your own request
              history remains available.
            </p>
          )}
          {canSource && (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={feedback.busy || inventory.isFetching}
                  onClick={() => {
                    clearDraft()
                    void inventory.refetch()
                  }}
                >
                  Refresh original subjects
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={offset === 0 || inventory.isFetching || feedback.busy}
                  onClick={() => {
                    clearDraft()
                    setOffset(Math.max(0, offset - 20))
                  }}
                >
                  Previous subjects
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={
                    !sourcePage ||
                    offset + 20 >= sourcePage.total ||
                    offset + 20 > 100000 ||
                    feedback.busy
                  }
                  onClick={() => {
                    clearDraft()
                    setOffset(offset + 20)
                  }}
                >
                  Next subjects
                </button>
              </div>
              {inventory.isFetching && <p role="status">Checking current original Tax subjects…</p>}
              {inventory.isError && <p role="alert">{inventory.error.message}</p>}
              {sourcePage?.items.length === 0 && <p>No eligible original HUF subjects found.</p>}
              <label className="block text-sm text-text-secondary space-y-1">
                <span>Original HUF subject</span>
                <select
                  className="input input-bordered w-full"
                  required
                  value={selected}
                  disabled={!sourcePage || feedback.busy}
                  onChange={event => {
                    clearDraft()
                    setSelected(event.target.value)
                  }}
                >
                  <option value="">Select an original subject</option>
                  {sourcePage?.items.map(item => (
                    <option key={item.facts.id} value={item.facts.id}>
                      {item.facts.customer_id} · {item.facts.period_basis} {item.facts.period_value}{' '}
                      · {item.facts.archived ? 'Archived' : 'Active'} · Revision{' '}
                      {item.facts.current_revision}
                    </option>
                  ))}
                </select>
              </label>
              {source && (
                <dl className="text-sm space-y-1 break-words">
                  <div>
                    <dt className="text-text-secondary">Original subject</dt>
                    <dd>
                      {source.facts.customer_id} · {source.facts.subject_type}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Original period</dt>
                    <dd>
                      {source.facts.period_basis} · {source.facts.period_value}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-text-secondary">Source state</dt>
                    <dd>
                      {source.facts.archived ? 'Archived' : 'Active'} · Revision{' '}
                      {source.facts.current_revision}
                    </dd>
                  </div>
                </dl>
              )}
            </>
          )}
          {source && (
            <fieldset className="space-y-4 min-w-0" disabled={feedback.busy}>
              <legend className="font-medium">Unverified declaration</legend>
              <Field
                label="Prospective controller’s account reference"
                required
                value={account}
                maxLength={36}
                onChange={event => {
                  setAccount(event.target.value)
                  setAck(null)
                }}
              />
              <p className="text-sm text-text-secondary">
                Ask the person to copy their reference from Client vault → Account sessions. Their
                legal authority and acceptance are not established by this reference.
              </p>
              <Field
                label="Declared capacity label"
                required
                value={capacity}
                maxLength={80}
                pattern="[A-Za-z][A-Za-z0-9._-]*"
                placeholder="For example: representative"
                onChange={event => {
                  setCapacity(event.target.value)
                  setAck(null)
                }}
              />
              <label className="block text-sm text-text-secondary space-y-1">
                <span>Requested legal role</span>
                <select
                  className="input input-bordered w-full"
                  required
                  value={role}
                  onChange={event => {
                    setRole(event.target.value)
                    setAck(null)
                  }}
                >
                  <option value="">Select a proposed role</option>
                  <option value="legal_controller">Legal controller</option>
                  <option value="representative">Representative</option>
                </select>
              </label>
              <fieldset className="space-y-2 min-w-0">
                <legend className="font-medium">Actions requested for verification</legend>
                <div className="flex flex-wrap gap-3">
                  {requestedActions.map(action => (
                    <label key={action} className="flex gap-2 text-sm text-text-secondary">
                      <input
                        type="checkbox"
                        checked={actions.includes(action)}
                        onChange={event => {
                          setActions(
                            event.target.checked
                              ? [...actions, action]
                              : actions.filter(value => value !== action)
                          )
                          setAck(null)
                        }}
                      />
                      {labels[action] ?? actionLabel(action)}
                    </label>
                  ))}
                </div>
              </fieldset>
              <fieldset className="space-y-3 min-w-0">
                <legend className="font-medium">Declared evidence references</legend>
                <p className="text-sm text-text-secondary">
                  Choose up to ten documents you can currently read. Only their references and
                  fingerprints are recorded. This request does not share file contents or verify
                  authenticity.
                </p>
                {!canEvidence && (
                  <p role="status">
                    Current Document-read permission is required to select evidence.
                  </p>
                )}
                {documents.isFetching && canEvidence && (
                  <p role="status">Checking current evidence references…</p>
                )}
                {documents.isError && canEvidence && <p role="alert">{documents.error.message}</p>}
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!canEvidence || documents.isFetching}
                    onClick={() => {
                      setDocumentIds([])
                      setAck(null)
                      void documents.refetch()
                    }}
                  >
                    Refresh evidence
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!evidencePage || documentPage === 1}
                    onClick={() => {
                      setDocumentIds([])
                      setAck(null)
                      setDocumentPage(documentPage - 1)
                    }}
                  >
                    Previous evidence
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={
                      !evidencePage ||
                      documentPage * 20 >= evidencePage.total ||
                      documentPage >= 100000
                    }
                    onClick={() => {
                      setDocumentIds([])
                      setAck(null)
                      setDocumentPage(documentPage + 1)
                    }}
                  >
                    Next evidence
                  </button>
                </div>
                {evidencePage?.items.map(item => (
                  <label key={item.id} className="flex items-start gap-2 text-sm break-words">
                    <input
                      type="checkbox"
                      checked={documentIds.includes(item.id)}
                      disabled={
                        !item.available ||
                        (!documentIds.includes(item.id) && documentIds.length >= 10)
                      }
                      onChange={event => {
                        setDocumentIds(
                          event.target.checked
                            ? [...documentIds, item.id]
                            : documentIds.filter(value => value !== item.id)
                        )
                        setAck(null)
                      }}
                    />
                    <span>
                      {item.name} · Version {item.version}
                      {!item.available && ' · Fingerprint unavailable or document deleted'}
                    </span>
                  </label>
                ))}
                {evidencePage?.items.length === 0 && (
                  <p>No currently readable evidence documents found.</p>
                )}
              </fieldset>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  required
                  disabled={!binding}
                  checked={!!binding && ack === binding}
                  onChange={event => setAck(event.target.checked ? binding : null)}
                />
                I reviewed this exact subject and declaration. This request is unverified and grants
                no ownership or data access.
              </label>
              <button
                className="btn btn-primary max-w-full whitespace-normal"
                disabled={!binding || ack !== binding || feedback.busy}
              >
                Request legal-subject verification
              </button>
            </fieldset>
          )}
        </form>
      )}
      <div className="border-t border-surface-overlay pt-4 space-y-3">
        <h3 className="font-semibold">Own request status</h3>
        <Field
          label="Saved request reference"
          value={requestInput}
          maxLength={36}
          onChange={event => {
            setRequestInput(event.target.value)
            setCancelAck(null)
          }}
        />
        <button
          className="btn btn-secondary"
          disabled={!isUuid(requestInput.trim()) || feedback.busy}
          onClick={() => {
            show(requestInput.trim().toLowerCase())
          }}
        >
          Check request status
        </button>
        {current.isFetching && request && <p role="status">Checking current request status…</p>}
        {current.isError && request && <p role="alert">{current.error.message}</p>}
        {currentReceipt && (
          <>
            <p role="status">
              {currentReceipt.state === 'pending' ? 'Pending verification' : 'Cancelled'} · No
              ownership or data access granted.
            </p>
            {currentReceipt.state === 'pending' && !originalCancel && (
              <div className="space-y-3">
                <label className="block text-sm text-text-secondary space-y-1">
                  <span>Cancellation reason</span>
                  <select
                    className="input input-bordered w-full"
                    value={cancelReason}
                    onChange={event => {
                      setCancelReason(event.target.value)
                      setCancelAck(null)
                    }}
                  >
                    <option value="">Select a reason</option>
                    <option value="withdrawn">Withdraw request</option>
                    <option value="incorrect_request">Incorrect request</option>
                    <option value="evidence_correction">Evidence needs correction</option>
                  </select>
                </label>
                <label className="flex gap-2 text-sm">
                  <input
                    type="checkbox"
                    disabled={!cancellationBinding}
                    checked={!!cancellationBinding && cancelAck === cancellationBinding}
                    onChange={event =>
                      setCancelAck(event.target.checked ? cancellationBinding : null)
                    }
                  />
                  Cancel this pending verification request.
                </label>
                <button
                  className="btn btn-secondary"
                  disabled={
                    !cancellationBinding || cancelAck !== cancellationBinding || feedback.busy
                  }
                  onClick={() => {
                    if (
                      !currentReceipt ||
                      !cancellationBinding ||
                      cancelAck !== cancellationBinding
                    )
                      return
                    const payload = {
                      request,
                      expected_ordinal: currentReceipt.ordinal,
                      reason: cancelReason,
                    }
                    cancel({
                      request,
                      body: {
                        expected_ordinal: payload.expected_ordinal,
                        reason: payload.reason,
                        idempotency_key: crypto.randomUUID(),
                      },
                    })
                  }}
                >
                  Cancel verification request
                </button>
              </div>
            )}
          </>
        )}
        {originalCancel && (
          <>
            <p role="status">Original cancellation is awaiting confirmation.</p>
            <button
              className="btn btn-secondary"
              disabled={feedback.busy}
              onClick={() => cancel(originalCancel)}
            >
              Retry original cancellation
            </button>
          </>
        )}
      </div>
    </section>
  )
}
